import React, {type MouseEvent, useCallback, useMemo, useState} from 'react';
import {IconButton, InputAdornment} from '@mui/material';
import type {TextFieldDiffProps} from 'components/fields/types';
import type {FieldError, PartText} from 'schemaDefinition/types';
import {evaluateFieldRequired} from 'schemaDefinition/functions';
import {Icon, TextField} from 'components';
import {
  toFieldError,
  validateSingleTextValue,
} from 'schema/form/functions/validators';
import {useAdornments} from 'schema/form/hooks';
import type {BaseFieldProps} from './types';
import {useDataFormContext} from '../../contexts';

type Props = BaseFieldProps & {
  part: PartText;
};

export const Text: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  label,
  placeholder,
  layout,
  focusableId,
  mode,
  diff,
  showWhenReadonlyAndEmpty,
  localScope,
  globalScope,
  relatedScope,
}) => {
  // const [localValue, setLocalValuel] = useState(value);

  const [visited, setVisited] = useState(false);
  const {prefix, suffix} = useAdornments(part, value);
  const handleSetVisited = useCallback(() => setVisited(true), []);
  const {showErrors} = useDataFormContext();

  const [isDirectEditMode, setIsDirectEditMode] = useState<boolean>();

  const handleToggleDirectEditMode = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDirectEditMode(prev => !prev);
  }, []);

  const required = useMemo(() => {
    return evaluateFieldRequired(
      part.required,
      valuePath,
      localScope,
      globalScope,
      relatedScope,
      value,
    );
  }, [globalScope, part.required, relatedScope, localScope, value, valuePath]);

  const error = useMemo((): FieldError => {
    if (showErrors || visited) {
      const valid = validateSingleTextValue(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
      }).valid;
      return toFieldError(valid);
    }
    return false;
  }, [
    globalScope,
    part,
    relatedScope,
    localScope,
    showErrors,
    value,
    valuePath,
    visited,
  ]);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      // Set empty values to null, to avoid sending empty strings to the backend
      const inputValue = event.target.value || null;
      setFieldValue(valuePath, inputValue, part);
    },
    [setFieldValue, valuePath, part],
  );

  const showEditToggle = !!part.editToggle && mode !== 'read-only';

  const endAdornment = useMemo(
    () =>
      showEditToggle ? (
        <>
          {suffix}
          <InputAdornment position="end">
            <IconButton
              data-cy={'button-edit'}
              color="primary"
              size="small"
              onClick={handleToggleDirectEditMode}>
              <Icon icon={isDirectEditMode ? 'CancelEdit' : 'Edit'} />
            </IconButton>
          </InputAdornment>
        </>
      ) : (
        suffix
      ),
    [handleToggleDirectEditMode, isDirectEditMode, showEditToggle, suffix],
  );

  return (
    <TextField
      name={valuePath}
      label={label}
      placeholder={placeholder}
      layout={layout}
      focusableId={focusableId}
      type="text"
      readonly={mode === 'read-only' || (showEditToggle && !isDirectEditMode)}
      diff={
        typeof diff?.compareValue === 'string'
          ? (diff as TextFieldDiffProps)
          : undefined
      }
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      required={required}
      value={value}
      slotProps={{
        input: {
          startAdornment: prefix,
          endAdornment,
        },
      }}
      onChange={handleChange}
      onBlur={handleSetVisited}
      error={error}
    />
  );
};
